import { ServiceError, unsupported } from '../errors'
import { pmsUrl, postJson } from '../http'
import { readLocal, writeLocal } from '../local-store'
import { clearPendingToPaidCache } from '../pagos/pendingToPaid'
import { hostOf, toStay } from '../reserva/real'
import type { ApiReserveDataGuestApp, Stay } from '../reserva/types'
import { parseMoney } from '../types'
import { kindOf } from './kind'
import { civitatisDestination, fetchCivitatisWidget } from './partner'
import type { Extra, ExtrasService } from './types'

/** `calculatepriceextra_guestapp` (E9), copiado de src/api/calculatepriceextra_guestapp/ de la app actual. */
interface ApiCalculatePriceExtra {
  ok: boolean
  status?: string
  extraList?: { id: string; name: string; price: string; exist: boolean }[]
}

/** `addextra_guestapp` (E10), copiado de src/api/addextra_guestapp/ de la app actual. */
interface ApiAddExtra {
  ok: boolean
  reserveDataGuestAppResponseList?: ApiReserveDataGuestApp[]
  status?: string
  statusType?: string
}

export const DISMISSED_FIELD = 'recommendationsDismissed'


export const extrasReal: ExtrasService = {
  paysInApp: false,
  partnerProvider: (stay) => (civitatisDestination(stay) ? 'civitatis' : null),
  async getPartnerWidget(stay, lang, signal) {
    const destination = civitatisDestination(stay)
    if (!destination) return unsupported()
    return fetchCivitatisWidget(destination, lang, signal)
  },
  async list(stay, signal) {
    const data = await postJson<ApiCalculatePriceExtra>(pmsUrl(stay.serverPath, 'calculatepriceextra_guestapp'), { reserveId: stay.id }, { auth: 'partner', ...(signal ? { signal } : {}) })
    if (!data.ok) throw new ServiceError('server', data.status)
    return (data.extraList ?? []).map(
      (e): Extra => ({
        id: e.id,
        name: e.name,
        kind: kindOf(e.name),
        description: null,
        photoUrl: null,
        price: parseMoney(e.price, stay.currency) ?? { amount: 0, currency: stay.currency },
        priceUnit: 'total',
        availability: null,
        conditions: null,
        when: null,
        alreadyAdded: Boolean(e.exist),
        partner: null,
      }),
    )
  },
  /** La API actual no tiene recomendación contextual (docs/backend/extras.md): estado vacío, nunca se deriva. */
  getRecommendation() {
    return Promise.resolve(null)
  },
  dismissRecommendation(stay, id) {
    const dismissed = readLocal<string[]>(stay.guestCode, DISMISSED_FIELD) ?? []
    writeLocal(stay.guestCode, DISMISSED_FIELD, [...new Set([...dismissed, id])])
    return Promise.resolve()
  },
  /**
   * AvailableExtrasView.tsx: E10 por extra con `{reserveId, extraId}`; `ok: false` es error; la reserva devuelta
   * sustituye a la anterior. La app actual añade uno cada vez; el carrito los añade en orden y se para en el primer
   * fallo (D46). El cobro lo hace el PMS fuera de la app, así que se invalida la copia de E2.
   */
  async checkout(stay, items) {
    const added: string[] = []
    let latest: Stay | null = null
    for (const [i, item] of items.entries()) {
      try {
        const data = await postJson<ApiAddExtra>(pmsUrl(stay.serverPath, 'addextra_guestapp'), { reserveId: stay.id, extraId: item.extraId }, { auth: 'partner' })
        if (!data.ok) throw new ServiceError('server', data.statusType, data.status)
        added.push(item.extraId)
        const reserve = data.reserveDataGuestAppResponseList?.[0]
        if (reserve) latest = toStay({ ...reserve, serverPath: hostOf(reserve.serverPath) })
      } catch (e) {
        if (added.length === 0) {
          clearPendingToPaidCache()
          throw e
        }
        clearPendingToPaidCache()
        return { mode: 'added', added, failed: item.extraId, notTried: items.slice(i + 1).map((it) => it.extraId), stay: latest }
      }
    }
    clearPendingToPaidCache()
    return { mode: 'added', added, failed: null, notTried: [], stay: latest }
  },
  // Sin cobro en la app ni pedidos con información práctica en la API actual (D46).
  waitForCheckout: unsupported,
  getCheckout: unsupported,
}
